import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { ClipboardCheck, Package, User } from "lucide-react";
import { cn } from "@/lib/utils";

interface Item {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  /** Color sólido del azulejo; el icono va en blanco encima. */
  color: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Menú «Más» del perito — mismo menú que el de admin/manager (`shell/MoreSheet`).
 *
 * Antes era una hoja inferior con una rejilla de tres columnas y tarjetas
 * bordeadas con el icono en color: nada que ver con el resto de la app. Ahora es
 * el panel flotante que se cuelga justo encima de la barra, con una fila por
 * página: azulejo de color macizo + icono BLANCO + nombre de la página.
 */
export function PeritoMoreSheet({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const items: Item[] = [
    { key: "seguimiento", label: t("perito.nav.seguimiento"), icon: ClipboardCheck, to: "/perito/seguimiento", color: "#6366F1" },
    { key: "inventario", label: t("perito.nav.inventario"), icon: Package, to: "/perito/inventario", color: "#0FA9B8" },
    // Perfil cierra la lista, en el mismo sitio que Ajustes en el menú de admin
    // (y con su mismo gris): es la última página del portal y no tenía ninguna
    // entrada en el móvil salvo tocar el avatar de la cabecera.
    { key: "perfil", label: t("perito.nav.perfil"), icon: User, to: "/perito/perfil", color: "#64748B" },
  ];

  if (!open) return null;

  const handle = (it: Item) => {
    onOpenChange(false);
    navigate(it.to);
  };

  return (
    <div
      className="lg:hidden fixed inset-0 z-40 bg-slate-950/10 backdrop-blur-[2px] animate-in fade-in"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="absolute left-0 right-0"
        // Se cuelga de las mismas variables que la barra: si la barra se mueve,
        // el menú la sigue en vez de quedarse descolgado.
        style={{ bottom: "calc(var(--dock-bottom) + var(--dock-height) + 10px)" }}
      >
        <div className="mx-auto max-w-md md:max-w-3xl px-3 md:px-6">
          <div
            onClick={(e) => e.stopPropagation()}
            className="ml-auto w-[min(20rem,88vw)] rounded-[26px] bg-white border border-[#E4EDF5] shadow-[0_18px_50px_rgba(20,60,100,0.22)] p-2 animate-in fade-in slide-in-from-bottom-2"
          >
            {items.map((it) => {
              const Icon = it.icon;
              const active = location.pathname.startsWith(it.to);
              return (
                <button
                  key={it.key}
                  onClick={() => handle(it)}
                  className={cn(
                    "w-full flex items-center gap-3.5 p-2.5 rounded-2xl hover:bg-[#F4F8FC] transition-colors text-left",
                    active && "bg-[#F4F8FC]",
                  )}
                >
                  <span
                    className="flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0"
                    style={{
                      backgroundColor: it.color,
                      boxShadow: `0 6px 14px ${it.color}42, inset 0 1px 0 rgba(255,255,255,0.28)`,
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </span>
                  <span className="font-bold text-[15px] text-foreground leading-tight">{it.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
